import {
  computePosition,
  flip,
  offset,
  type ReferenceElement,
  shift,
  size,
} from '@floating-ui/dom';
import { render, type TemplateResult } from 'lit';

/** How long a touch rests on an element before it opens the element's menu as a sheet. */
export const TOOLBAR_LONG_PRESS_MS = 500;

/** Distance a resting touch may drift before it counts as a pan instead of a long press. */
export const TOOLBAR_LONG_PRESS_SLOP = 8;

/** Gap of the sheet to the view's edges. */
const SHEET_INSET = 8;

/**
 * An element's "More" menu, opened at the pointer by a right click on the element or as a sheet
 * at the view's bottom by a long press. It closes on a pointer down outside, Escape, or a chosen item.
 */
export class ToolbarPointerMenu {
  readonly element = document.createElement('div');

  private _close: (() => void) | null = null;

  constructor(private readonly _root: ShadowRoot) {
    this.element.className = 'pointer-menu';
    this.element.setAttribute('role', 'menu');
  }

  get isOpen() {
    return this._close !== null;
  }

  /**
   * @param anchor the pointer (a zero-size rect) or, for the sheet, the view the sheet spans
   */
  open(content: TemplateResult, anchor: DOMRect, sheet: boolean) {
    this.close();
    const { element } = this;
    render(content, element);
    element.toggleAttribute('data-sheet', sheet);
    this._root.append(element);

    const reference: ReferenceElement = sheet
      ? {
          getBoundingClientRect: () =>
            new DOMRect(
              anchor.left + SHEET_INSET,
              anchor.bottom - SHEET_INSET,
              anchor.width - 2 * SHEET_INSET,
              0
            ),
        }
      : { getBoundingClientRect: () => anchor };
    computePosition(
      reference,
      element,
      sheet
        ? {
            placement: 'top',
            middleware: [
              size({
                apply: ({ rects, elements }) => {
                  elements.floating.style.width = `${rects.reference.width}px`;
                },
              }),
            ],
          }
        : {
            placement: 'bottom-start',
            middleware: [
              offset(4),
              flip({ padding: 8 }),
              shift({ padding: 8 }),
            ],
          }
    )
      .then(({ x, y }) => {
        element.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      })
      .catch(console.error);

    const onPointerDown = (event: PointerEvent) => {
      if (!event.composedPath().includes(element)) this.close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') this.close();
    };
    // An item closes the menu once its action ran (its own click listener comes first); buttons that
    // open a nested menu (a colour picker) keep it open.
    const onClick = (event: MouseEvent) => {
      const path = event.composedPath();
      const chose = path.some(
        target =>
          target instanceof Element &&
          (target.localName === 'editor-menu-action' ||
            target.localName === 'editor-icon-button')
      );
      const nested = path.some(
        target =>
          target instanceof Element && target.localName === 'editor-menu-button'
      );
      if (chose && !nested) this.close();
    };
    const doc = element.ownerDocument;
    doc.addEventListener('pointerdown', onPointerDown, true);
    doc.addEventListener('keydown', onKeyDown, true);
    element.addEventListener('click', onClick);
    this._close = () => {
      doc.removeEventListener('pointerdown', onPointerDown, true);
      doc.removeEventListener('keydown', onKeyDown, true);
      element.removeEventListener('click', onClick);
      render(null, element);
      element.remove();
    };
  }

  close() {
    const close = this._close;
    this._close = null;
    close?.();
  }
}
